import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type ISocketOrderEvent } from '@/types/ISocketOrderEvent';
import { type INotification } from '@/types/INotification';
import { isNewerOrderEvent } from '@/utils/isNewerOrderEvent';
import { createOrderNotification } from '@/utils/createOrderNotification';
import { isSameLocalDay } from '@/utils/isSameLocalDay';
import {
  getNotificationsHistory,
  addNotificationToHistory,
  markNotificationRead,
  markOrderNotificationRead,
  markAllNotificationsRead,
  clearNotificationsHistory
} from '@/services/notifications/notificationsHistoryStorage';

export interface INotificationsState {
  /** Эфемерный слой: видимые тосты (авто-скрытие, не персистится). */
  toasts: INotification[];
  /** Постоянная история для панели — зеркало localStorage. */
  history: INotification[];
  /** Раскрыт ли схлопнутый тост-сводка (>2 тостов). */
  isExpanded: boolean;
  /** Последнее обработанное событие заказа — для дедупа более новых. */
  lastEvent: ISocketOrderEvent | null;
}

const initialState: INotificationsState = {
  toasts: [],
  history: getNotificationsHistory(),
  isExpanded: false,
  lastEvent: null
};

const notifications = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    enqueueOrderNotification(state, action: PayloadAction<ISocketOrderEvent>) {
      const event = action.payload;

      if (!isNewerOrderEvent(event, state.lastEvent)) {
        return;
      }

      state.lastEvent = event;

      const notification = createOrderNotification(event);
      if (state.history.some((item) => item.id === notification.id)) {
        return;
      }

      state.toasts.push(notification);
      state.history = addNotificationToHistory(notification);
    },
    /** Скрывает тост, не затрагивая историю (запись остаётся в панели). */
    dismissToast(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter((item) => item.id !== action.payload);
    },
    clearToasts(state) {
      state.toasts = [];
      state.isExpanded = false;
    },
    setExpanded(state, action: PayloadAction<boolean>) {
      state.isExpanded = action.payload;
    },
    markRead(state, action: PayloadAction<string>) {
      state.history = markNotificationRead(action.payload);
    },
    /** Помечает прочитанным уведомление заказа по uuid (открытие заказа). */
    markOrderRead(state, action: PayloadAction<string>) {
      state.history = markOrderNotificationRead(action.payload);
    },
    markAllRead(state) {
      state.history = markAllNotificationsRead();
    },
    /** Очистка истории пользователем (кнопка «Очистить» в панели). Дедуп
     *  (`lastEvent`) сохраняется, чтобы уже показанное событие не повторилось. */
    clearHistory(state) {
      clearNotificationsHistory();
      state.history = [];
    },
    /** Полная очистка тостов и истории (логаут). */
    clearAll(state) {
      clearNotificationsHistory();
      state.toasts = [];
      state.history = [];
      state.isExpanded = false;
      state.lastEvent = null;
    },
    /**
     * Логаут: историю НЕ удаляем (переживает перелогин в рамках дня), но
     * помечаем всё прочитанным, чтобы счётчик/favicon не поднимались от старых
     * записей. Эфемерное (тосты, раскрытие, дедуп) сбрасываем. Суточная чистка
     * сама уберёт ленту через день.
     */
    markAllReadOnLogout(state) {
      state.history = markAllNotificationsRead();
      state.toasts = [];
      state.isExpanded = false;
      state.lastEvent = null;
    },
    /** Ленивая суточная чистка: оставляет записи текущего локального дня. */
    pruneStale(state) {
      const now = Date.now();
      state.history = state.history.filter((item) =>
        isSameLocalDay(item.createdAt, now)
      );
    },
    /** Ресинхронизация истории с localStorage (другая вкладка, событие storage). */
    syncHistory(state) {
      state.history = getNotificationsHistory();
    }
  }
});

export const {
  enqueueOrderNotification,
  dismissToast,
  clearToasts,
  setExpanded,
  markRead,
  markOrderRead,
  markAllRead,
  clearHistory,
  clearAll,
  markAllReadOnLogout,
  pruneStale,
  syncHistory
} = notifications.actions;

export default notifications.reducer;
